/*
 * Reevam product discount preview.
 * Uses an isolated Shopify Storefront API cart to ask Shopify which product
 * lines receive automatic discounts and what their final prices are.
 * It never reads or changes the shopper's real cart.
 */
(() => {
  const SHOP_DOMAIN = "dg1kzh-68.myshopify.com";
  const API_VERSION = "2026-01";
  const API_URL = "https://" + SHOP_DOMAIN + "/api/" + API_VERSION + "/graphql.json";
  const CART_PREVIEW_QUERY =
    'mutation ReevamPricePreview($input: CartInput!) {' +
    ' cartCreate(input: $input) {' +
    '  cart {' +
    '   lines(first: 250) {' +
    '    nodes {' +
    '     merchandise { ... on ProductVariant { id product { id handle } } }' +
    '     cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }' +
    '     discountAllocations(lineLevelOnly: true) {' +
    '      __typename' +
    '      ... on CartAutomaticDiscountAllocation { title discountedAmount { amount currencyCode } }' +
    '     }' +
    '    }' +
    '   }' +
    '  }' +
    '  userErrors { field message }' +
    ' }' +
    '}';

  const asString = value => String(value == null ? "" : value);
  const lastId = value => {
    const match = asString(value).match(/(\d+)$/);
    return match ? match[1] : asString(value);
  };
  const toGid = (type, value) => {
    const v = asString(value).trim();
    if (!v) return "";
    if (v.startsWith("gid://shopify/")) return v;
    return "gid://shopify/" + type + "/" + lastId(v);
  };
  const normalizeTitle = value => asString(value).toLowerCase().replace(/\b\d+(?:\.\d+)?\s*%/g, " percentage ").replace(/[^a-z0-9]+/g, " ").trim();

  function currencyFormat(amount, currency) {
    const n = Number(amount || 0);
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n);
    } catch (_) {
      return (currency || "INR") + " " + n.toFixed(2);
    }
  }

  function activeOffers() {
    const node = document.querySelector("[data-reevam-discount-offers]");
    if (!node) return [];
    let source;
    try { source = JSON.parse(node.textContent || "[]"); } catch (_) { return []; }
    const now = Math.floor(Date.now() / 1000);
    return (Array.isArray(source) ? source : []).filter(offer => {
      if (!offer || offer.active !== true) return false;
      if (offer.startsAt != null && Number(offer.startsAt) > now) return false;
      if (offer.endsAt != null && Number(offer.endsAt) < now) return false;
      return true;
    }).map(offer => ({
      id: asString(offer.id),
      title: asString(offer.title).trim(),
      type: asString(offer.type),
      badgeLabel: asString(offer.badgeLabel).trim(),
      valueLabel: asString(offer.valueLabel).trim()
    }));
  }

  function addVariant(map, id) {
    const gid = toGid("ProductVariant", id);
    if (gid) map.set(gid, { id: gid });
  }

  function collectVariants() {
    const variants = new Map();

    document.querySelectorAll("[data-reevam-discount-catalog]").forEach(node => {
      try {
        const data = JSON.parse(node.textContent || "{}");
        (data.products || []).forEach(product => (product.variants || []).forEach(id => addVariant(variants, id)));
      } catch (_) {}
    });

    document.querySelectorAll("[data-reevam-price-container]").forEach(container => {
      addVariant(variants, container.dataset.reevamVariantId);
      try {
        const ids = JSON.parse(container.dataset.reevamVariantIds || "[]");
        if (Array.isArray(ids)) ids.forEach(id => addVariant(variants, id));
      } catch (_) {}
    });

    return Array.from(variants.values()).slice(0, 240);
  }

  function titleMatches(a, b) {
    const first = normalizeTitle(a);
    const second = normalizeTitle(b);
    return Boolean(first && second && first === second);
  }

  function matchingAllocations(line, offer) {
    if (!line || !offer) return [];
    return (line.discountAllocations || []).filter(allocation =>
      allocation &&
      allocation.__typename === "CartAutomaticDiscountAllocation" &&
      titleMatches(allocation.title, offer.title) &&
      Number(allocation.discountedAmount && allocation.discountedAmount.amount || 0) > 0
    );
  }

  function lineMoney(line) {
    const subtotal = Number(line && line.cost && line.cost.subtotalAmount && line.cost.subtotalAmount.amount || 0);
    const total = Number(line && line.cost && line.cost.totalAmount && line.cost.totalAmount.amount || 0);
    const currency = asString(line && line.cost && line.cost.totalAmount && line.cost.totalAmount.currencyCode || "INR");
    return { subtotal, total, currency };
  }

  function offerPercent(line, offer) {
    const money = lineMoney(line);
    if (money.subtotal <= 0) return 0;
    const saved = matchingAllocations(line, offer).reduce((sum, item) =>
      sum + Number(item.discountedAmount && item.discountedAmount.amount || 0), 0);
    return Math.max(0, Math.round((saved / money.subtotal) * 100));
  }

  async function fetchPreviewCart(variants) {
    const allLines = [];
    for (let i = 0; i < variants.length; i += 200) {
      const batch = variants.slice(i, i + 200);
      const response = await fetch(API_URL, {
        method: "POST",
        credentials: "omit",
        cache: "no-store",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          query: CART_PREVIEW_QUERY,
          variables: {
            input: {
              buyerIdentity: { countryCode: "IN" },
              lines: batch.map(variant => ({ merchandiseId: variant.id, quantity: 1 }))
            }
          }
        })
      });
      if (!response.ok) throw new Error("Storefront Cart API returned HTTP " + response.status);
      const payload = await response.json();
      if (payload.errors && payload.errors.length) throw new Error(payload.errors.map(error => error.message).join("; "));
      const result = payload.data && payload.data.cartCreate;
      if (!result) throw new Error("Shopify did not return a preview cart.");
      if (result.userErrors && result.userErrors.length) {
        throw new Error(result.userErrors.map(error => error.message).join("; "));
      }
      allLines.push(...((result.cart && result.cart.lines && result.cart.lines.nodes) || []));
    }
    return allLines.map(line => {
      const variant = line.merchandise || {};
      return {
        variantId: asString(variant.id),
        productId: asString(variant.product && variant.product.id),
        handle: asString(variant.product && variant.product.handle),
        cost: lineMoney(line),
        discountAllocations: line.discountAllocations || []
      };
    }).filter(line => line.variantId && line.productId);
  }

  let cachedSignature = "";
  let cachedPromise = null;
  function previewLines() {
    const variants = collectVariants();
    if (!variants.length) return Promise.resolve([]);
    const signature = variants.map(v => v.id).sort().join("|");
    if (signature === cachedSignature && cachedPromise) return cachedPromise;
    cachedSignature = signature;
    cachedPromise = fetchPreviewCart(variants).catch(error => {
      cachedSignature = "";
      cachedPromise = null;
      throw error;
    });
    return cachedPromise;
  }

  function offerForLine(line, offers) {
    const automaticAllocations = (line && line.discountAllocations || []).filter(allocation =>
      allocation &&
      allocation.__typename === "CartAutomaticDiscountAllocation" &&
      Number(allocation.discountedAmount && allocation.discountedAmount.amount || 0) > 0 &&
      asString(allocation.title).trim()
    );
    if (!automaticAllocations.length) return null;

    for (const offer of offers) {
      if (offer.type !== "automatic_percentage") continue;
      if (matchingAllocations(line, offer).length) return offer;
    }

    // Fall back to Shopify's actual line allocation if this discount has not
    // yet been added to Reevam's display metadata. Price/eligibility remain live.
    const allocation = automaticAllocations[0];
    return {
      id: "",
      title: asString(allocation.title).trim(),
      type: "automatic_percentage",
      badgeLabel: "",
      valueLabel: "",
      source: "storefront-cart"
    };
  }

  function setText(node, value) {
    if (node && node.textContent !== asString(value)) node.textContent = asString(value);
  }

  function setBadgeLabel(node, value) {
    if (!node) return;
    const parts = asString(value).trim().split(/\\s+/).filter(Boolean);
    node.textContent = "";
    if (!parts.length) return;
    const first = document.createElement("strong");
    first.textContent = parts.shift();
    first.style.fontWeight = "750";
    node.appendChild(first);
    if (parts.length) {
      const rest = document.createElement("span");
      rest.textContent = parts.join(" ");
      rest.style.fontWeight = "500";
      node.appendChild(document.createTextNode(" "));
      node.appendChild(rest);
    }
  }

  function findLineForVariant(lines, variantId) {
    const key = toGid("ProductVariant", variantId);
    return lines.find(line => line.variantId === key) || null;
  }

  function renderPrice(container, lines, offers) {
    const native = container.querySelector(".reevam-native-price");
    const live = container.querySelector("[data-reevam-live-price]");
    if (!native || !live) return;

    const isProductPage = container.dataset.reevamPageType === "product";
    const isHomePage = document.querySelector('#MainContent[data-template="index"]') !== null;
    let candidates = [];
    if (isProductPage) {
      const selected = findLineForVariant(lines, container.dataset.reevamVariantId);
      if (selected) candidates = [selected];
    } else {
      let ids = [];
      try { ids = JSON.parse(container.dataset.reevamVariantIds || "[]"); } catch (_) {}
      if (!Array.isArray(ids)) ids = [];
      candidates = ids.map(id => findLineForVariant(lines, id)).filter(Boolean);
      if (!candidates.length) {
        const selected = findLineForVariant(lines, container.dataset.reevamVariantId);
        if (selected) candidates = [selected];
      }
    }

    const eligible = candidates.map(line => ({ line, offer: offerForLine(line, offers) })).filter(item => item.offer);
    if (!eligible.length) {
      const fallback = native.querySelector("[data-reevam-fallback-discount]");
      const regularFallback = native.querySelector("[data-reevam-native-regular-price]");
      if (fallback) fallback.hidden = true;
      if (regularFallback) regularFallback.hidden = false;
      native.hidden = false;
      live.hidden = true;
      return;
    }

    const priceEntries = candidates
      .map(line => ({ line, money: lineMoney(line) }))
      .filter(entry => entry.money.subtotal > 0);
    if (!priceEntries.length) {
      native.hidden = false;
      live.hidden = true;
      return;
    }
    const values = priceEntries.map(entry => entry.money);
    const lowestEntry = priceEntries.reduce((lowest, entry) =>
      entry.money.subtotal < lowest.money.subtotal ? entry : lowest, priceEntries[0]);
    const finals = values.map(value => value.total);
    const originals = values.map(value => value.subtotal);
    const minFinal = Math.min(...finals);
    const maxFinal = Math.max(...finals);
    const minOriginal = Math.min(...originals);
    const maxOriginal = Math.max(...originals);
    const currency = values[0].currency || "INR";
    const firstEligible = eligible[0];
    const pct = isProductPage
      ? offerPercent(eligible[0].line, eligible[0].offer)
      : Math.max(...eligible.map(item => offerPercent(item.line, item.offer)));

    const finalNode = live.querySelector("[data-reevam-live-final]");
    const originalNode = live.querySelector("[data-reevam-live-original]");
    const percentNode = live.querySelector("[data-reevam-live-percent]");
    setText(finalNode, isHomePage
      ? currencyFormat(lowestEntry.money.total, lowestEntry.money.currency || currency)
      : (minFinal === maxFinal
        ? currencyFormat(minFinal, currency)
        : currencyFormat(minFinal, currency) + " – " + currencyFormat(maxFinal, currency)));
    setText(originalNode, isHomePage
      ? currencyFormat(lowestEntry.money.subtotal, lowestEntry.money.currency || currency)
      : (minOriginal === maxOriginal
        ? currencyFormat(minOriginal, currency)
        : currencyFormat(minOriginal, currency) + " – " + currencyFormat(maxOriginal, currency)));
    if (originalNode) originalNode.hidden = maxFinal >= maxOriginal && minFinal >= minOriginal;
    if (percentNode) {
      percentNode.hidden = pct <= 0;
      setText(percentNode, pct > 0 ? pct + "% OFF" : "");
    }

    native.hidden = true;
    live.hidden = false;
  }

  function renderBadges(lines, offers) {
    const discountedProducts = new Map();
    lines.forEach(line => {
      const offer = offerForLine(line, offers);
      if (!offer) return;
      const key = lastId(line.productId);
      if (!discountedProducts.has(key)) discountedProducts.set(key, { offer, percent: offerPercent(line, offer), line });
    });

    document.querySelectorAll("[data-reevam-discount-badge][data-reevam-product-id]").forEach(badge => {
      const key = lastId(badge.dataset.reevamProductId);
      const match = discountedProducts.get(key);
      const variant = badge.dataset.reevamVariantId;
      const exactLine = variant ? findLineForVariant(lines, variant) : null;
      const exactOffer = exactLine ? offerForLine(exactLine, offers) : null;
      if (variant && exactLine && !exactOffer) {
        badge.hidden = true;
        badge.textContent = "";
        const nearestCard = badge.closest("[data-reevam-product-badges]");
        if (nearestCard) {
          const nativeSale = nearestCard.querySelector("[data-reevam-native-sale-badge]");
          if (nativeSale) nativeSale.hidden = false;
        }
        return;
      }
      if (!match) {
        badge.hidden = true;
        badge.textContent = "";
        badge.removeAttribute("title");
        badge.removeAttribute("aria-label");
        const nearestCard = badge.closest("[data-reevam-product-badges]");
        if (nearestCard) {
          const nativeSale = nearestCard.querySelector("[data-reevam-native-sale-badge]");
          if (nativeSale) nativeSale.hidden = false;
        }
        return;
      }
      const offer = exactOffer || match.offer;
      const verifiedPercent = exactLine && exactOffer ? offerPercent(exactLine, exactOffer) : match.percent;
      const label = offer.badgeLabel || (verifiedPercent > 0 ? verifiedPercent + "% OFF" : (offer.valueLabel || offer.title || "Offer"));
      setText(badge, label);
      badge.title = offer.title;
      badge.setAttribute("aria-label", label + " — " + offer.title);
      badge.hidden = false;
      const nearestCard = badge.closest("[data-reevam-product-badges]");
      if (nearestCard) {
        const nativeSale = nearestCard.querySelector("[data-reevam-native-sale-badge]");
        if (nativeSale) nativeSale.hidden = true;
      }
    });
  }

  function renderOfferCounts(lines, offers) {
    document.querySelectorAll("[data-reevam-offer-card][data-reevam-offer-type='automatic_percentage']").forEach(card => {
      const title = card.dataset.reevamOfferTitle || "";
      const offer = offers.find(item => item.type === "automatic_percentage" && titleMatches(item.title, title));
      if (!offer) return;
      const eligibleProducts = new Set();
      const percents = [];
      let verifiedTitle = "";
      lines.forEach(line => {
        const allocations = matchingAllocations(line, offer);
        if (!allocations.length) return;
        eligibleProducts.add(lastId(line.productId));
        if (!verifiedTitle) verifiedTitle = asString(allocations[0].title).trim();
        const pct = offerPercent(line, offer);
        if (pct > 0) percents.push(pct);
      });
      const count = eligibleProducts.size;
      const percentage = percents.length ? Math.max(...percents) : 0;
      const value = card.querySelector("[data-reevam-offer-value]");
      const heading = card.querySelector("h3");
      const description = card.querySelector("[data-reevam-offer-description]");
      const eligibility = card.querySelector("[data-reevam-offer-eligibility]");
      if (verifiedTitle && heading) setText(heading, verifiedTitle);
      if (percentage > 0) {
        setText(value, percentage + "% OFF");
        setText(description, percentage + "% off " + count + (count === 1 ? " product" : " products"));
        setText(eligibility, count + (count === 1 ? " selected product" : " selected products"));
        if (description) description.removeAttribute("data-reevam-offer-error");
        if (eligibility) eligibility.removeAttribute("data-reevam-offer-error");
      } else {
        // Keep the theme's verified Admin-side offer summary if the preview cart has no purchasable
        // variants to calculate (for example, temporarily sold-out selected styles).
      }
    });
  }

  function renderAll(lines) {
    const offers = activeOffers();
    document.documentElement.dataset.reevamDiscountPreview = "ready";
    document.querySelectorAll("[data-reevam-price-container]").forEach(container => renderPrice(container, lines, offers));
    renderBadges(lines, offers);
    renderOfferCounts(lines, offers);
    window.__reevamDiscountPreview = {
      status: "ready",
      variantCount: lines.length,
      eligibleProducts: Array.from(new Set(lines.filter(line => offerForLine(line, offers)).map(line => lastId(line.productId)))).length,
      offers: offers.map(offer => ({ title: offer.title, badgeLabel: offer.badgeLabel }))
    };
  }

  function renderError(error) {
    document.documentElement.dataset.reevamDiscountPreview = "error";
    document.querySelectorAll("[data-reevam-price-container]").forEach(container => {
      const native = container.querySelector(".reevam-native-price");
      const live = container.querySelector("[data-reevam-live-price]");
      const fallback = native && native.querySelector("[data-reevam-fallback-discount]");
      const regularFallback = native && native.querySelector("[data-reevam-native-regular-price]");
      if (fallback) fallback.hidden = false;
      if (regularFallback) regularFallback.hidden = true;
      if (native) native.hidden = false;
      if (live) live.hidden = true;
    });
    document.querySelectorAll("[data-reevam-discount-badge]").forEach(badge => {
      if (badge.hasAttribute("data-reevam-fallback-discount-badge")) {
        badge.hidden = false;
        if (!badge.textContent.trim()) setText(badge, "Launch Sale");
      } else {
        badge.hidden = true;
        badge.textContent = "";
      }
    });
    document.querySelectorAll("[data-reevam-native-sale-badge]").forEach(badge => {
      const fallback = badge.closest("[data-reevam-product-badges]")?.querySelector("[data-reevam-fallback-discount-badge]");
      badge.hidden = Boolean(fallback && !fallback.hidden);
    });
    window.__reevamDiscountPreview = { status: "error", message: asString(error && error.message || error) };
    if (window.console && console.warn) console.warn("[Reevam discount preview]", error);
  }

  // Mobile announcement bar: rotate active offers so they never stack onto two lines.
  function initAnnouncementRotator() {
    document.querySelectorAll("[data-reevam-announcement]").forEach(wrapper => {
      if (wrapper.dataset.reevamRotatorInitialized === "true") return;
      const items = Array.from(wrapper.querySelectorAll("[data-reevam-announcement-item]"));
      if (items.length < 2) return;

      wrapper.dataset.reevamRotatorInitialized = "true";
      const firstActiveIndex = items.findIndex(item => item.classList.contains("is-active"));
      let activeIndex = firstActiveIndex >= 0 ? firstActiveIndex : 0;
      const mobileQuery = window.matchMedia("(max-width: 749px)");
      const interval = Math.max(2000, Number(wrapper.dataset.reevamAnnouncementInterval) || 4000);

      function applyLayout() {
        if (!mobileQuery.matches) {
          wrapper.classList.remove("reevam-announcement__offers--mobile-rotating");
          items.forEach(item => {
            item.classList.remove("is-active");
            item.removeAttribute("aria-hidden");
          });
          return;
        }

        wrapper.classList.add("reevam-announcement__offers--mobile-rotating");
        items.forEach((item, index) => {
          const active = index === activeIndex;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-hidden", active ? "false" : "true");
        });
      }

      applyLayout();
      if (mobileQuery.addEventListener) mobileQuery.addEventListener("change", applyLayout);
      else if (mobileQuery.addListener) mobileQuery.addListener(applyLayout);

      window.setInterval(() => {
        if (!mobileQuery.matches || document.hidden) return;
        activeIndex = (activeIndex + 1) % items.length;
        applyLayout();
      }, interval);
    });
  }

  let refreshTimer = 0;
  let inflight = false;
  async function refresh() {
    if (inflight) return;
    inflight = true;
    try {
      const lines = await previewLines();
      renderAll(lines);
    } catch (error) {
      renderError(error);
    } finally {
      inflight = false;
    }
  }

  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 120);
  }

  document.addEventListener("change", event => {
    const target = event.target;
    if (target && target.matches && target.matches('select[name="id"], input[name="id"], variant-selects select, variant-radios input')) {
      scheduleRefresh();
    }
  }, true);
  ["variant:change", "product:variant-change", "shopify:section:load"].forEach(name => document.addEventListener(name, scheduleRefresh));
  window.addEventListener("pageshow", scheduleRefresh);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAnnouncementRotator();
      scheduleRefresh();
    }, { once: true });
  } else {
    initAnnouncementRotator();
    scheduleRefresh();
  }
  document.addEventListener("shopify:section:load", initAnnouncementRotator);

  if ("MutationObserver" in window && document.documentElement) {
    const observer = new MutationObserver(records => {
      if (records.some(record => record.type === "attributes" && record.attributeName === "data-reevam-variant-id")) {
        scheduleRefresh();
      }
    });
    observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ["data-reevam-variant-id"] });
  }
})();